import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@shared/contexts/LanguageContext";
import { useConfig, CONFIG_FALLBACK } from "@shared/api/hooks/useConfig";
import { resolveByCode } from "@shared/api/byCode";
import { Hash, Menu, QrCode } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { toast } from "sonner";
import PreferencesPanel from "@visitor/components/PreferencesPanel";

// F-B / Q-OQ-B (locked 2026-06-15):
//   - Single round trip via `/api/by-code/{code}` returns {kind, id, code}
//     so we dispatch to /exhibition/:id, /zone/:id, or /exhibit/:id without
//     the legacy "probe + 404 fallback" pattern.
//   - Code width is config-driven (`GET /api/v1/config`) so a future
//     `CODE_DIGITS=4` deploy widens input without a code change.
// ADR-0022:
//   - Auto-submit-at-3 replaced by an explicit Submit button (needed for
//     variable widths); button enabled when code.length ≥ codeMinDigits.

const DX_LOGO_URL =
  "https://cdn.art-mate.net/uploads/artmate/202502/thumbnail/20250228_160227_1YqJgAgCUS_p_300_0.png";

const CodeEntry = () => {
  const { fontSize, t, setAccessMethod } = useLanguage();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const { data: cfg = CONFIG_FALLBACK } = useConfig();

  const fontSizeClass =
    fontSize === "small"
      ? "text-sm"
      : fontSize === "large"
        ? "text-xl"
        : "text-base";

  const handleSubmit = async () => {
    if (code.length < cfg.codeMinDigits) return;
    setLoading(true);
    setAccessMethod("code");
    try {
      const out = await resolveByCode(code);
      if (out.kind === "exhibition") {
        navigate(`/exhibition/${out.id}`);
      } else if (out.kind === "zone") {
        navigate(`/zone/${out.id}`);
      } else {
        navigate(`/exhibit/${out.id}`);
      }
    } catch (error) {
      // axios + ApiError both expose response.status; tolerate either.
      const status =
        error?.response?.status ??
        (error?.message?.includes("404") ? 404 : undefined);
      if (status === 404 || status === 400) {
        toast.error(
          t({
            en: "Exhibit Code not found. Please check it.",
            tc: "找不到展品代碼，請檢查代碼",
            sc: "找不到展品代码，请检查代码",
          }),
        );
      } else {
        toast.error(
          t({
            en: "Error accessing content",
            tc: "無法存取內容",
            sc: "无法访问内容",
          }),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, cfg.codeMaxDigits);
    setCode(value);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const submitDisabled = code.length < cfg.codeMinDigits || loading;

  return (
    <div
      className="min-h-screen flex flex-col bg-black"
      data-testid="code-entry-page"
    >
      <div className="sticky top-0 bg-black/90 backdrop-blur border-b border-white/10 z-10">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between relative">
          <img
            src={DX_LOGO_URL}
            alt="DX Logo"
            className="w-28 object-contain"
          />
          <button
            onClick={() => setPreferencesOpen(true)}
            className="text-white hover:bg-white/10 rounded-lg p-2"
            data-testid="open-preferences"
          >
            <Menu className="w-8 h-8" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="self-start">
          <h1 className="absolute left-1/2 -translate-x-1/2 text-2xl font-heading font-bold text-white">
            {t({
              en: "Enter Exhibit Code",
              tc: "輸入展品代碼",
              sc: "输入展品代码",
            })}
          </h1>
        </div>
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center">
              <Hash className="w-10 h-10 text-white" />
            </div>
            <p className={`text-center text-white/70 ${fontSizeClass}`}>
              {t({
                en: "Enter the Exhibit Code displayed near the exhibit",
                tc: "輸入展品旁顯示的展品代碼",
                sc: "输入展品旁显示的展品代码",
              })}
            </p>
          </div>

          <div className="space-y-6">
            <div className="flex justify-center">
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={code}
                onChange={handleCodeChange}
                onKeyDown={onKeyDown}
                placeholder={"0".repeat(cfg.codeDigits)}
                className="w-48 h-24 text-6xl text-center font-mono tracking-widest bg-white/10 border-white/20 text-white placeholder:text-white/30"
                maxLength={cfg.codeMaxDigits}
                autoFocus
                data-testid="code-input"
              />
            </div>

            <div className="flex justify-center">
              <Button
                onClick={handleSubmit}
                disabled={submitDisabled}
                className="px-8"
                data-testid="code-submit-button"
              >
                {t({ en: "Submit", tc: "提交", sc: "提交" })}
              </Button>
            </div>

            {loading && (
              <p className={`text-center text-white/60 ${fontSizeClass}`}>
                {t({ en: "Loading...", tc: "載入中...", sc: "加载中..." })}
              </p>
            )}
          </div>

          <div className="text-center space-y-4">
            <p className={`text-white/50 ${fontSizeClass}`}>
              {t({
                en: "Exhibit Code example: 123, 045, 789",
                tc: "展品代碼範例：123、045、789",
                sc: "展品代码示例：123、045、789",
              })}
            </p>

            <button
              onClick={() => {
                setAccessMethod("qr");
                navigate("/qr-instructions");
              }}
              className="flex items-center justify-center gap-3 mx-auto px-6 py-3 rounded-xl bg-white/5 border border-white/20 hover:bg-white/10 transition-all"
              data-testid="scan-qr-instead"
            >
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <QrCode className="w-7 h-7 text-white" />
              </div>
              <span className={`text-white font-medium ${fontSizeClass}`}>
                {t({
                  en: "Scan QR code instead",
                  tc: "改用掃描二維碼",
                  sc: "改用扫描二维码",
                })}
              </span>
            </button>
          </div>
        </div>
      </div>

      <PreferencesPanel
        isOpen={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
      />
    </div>
  );
};

export default CodeEntry;
